import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useToast } from '../../contexts/ToastContext';
import sessionService from '../../services/sessionService';
import centresService from '../../services/centresService';
import auditLogService from '../../services/auditLogService';
import { BookOpen, User, Users, Calendar, History, FileText, GitCommit, Pencil, Upload, Download, Trash2, Paperclip, Eye, ExternalLink } from 'lucide-react';
import ConsolidatedOrderDownloadButton from '../../components/ConsolidatedOrderDownloadButton/ConsolidatedOrderDownloadButton';
import InvoiceModal from '../../components/InvoiceModal/InvoiceModal';
import { formatMoney } from '../../utils/money';
import './SessionDetailPage.css';

const SessionDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isResponsable, isAdmin, isManager, isLogistiqueCoordinator, isFournisseur, user } = useAuth();
  const { canView, canEdit, canDownload } = usePermissions();
  const toast = useToast();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [managerModifNote, setManagerModifNote] = useState('');
  const [showManagerModifForm, setShowManagerModifForm] = useState(false);
  const [logNote, setLogNote] = useState('');
  const [logRejectReason, setLogRejectReason] = useState('');
  const [showLogNoteForm, setShowLogNoteForm] = useState(false);
  const [showLogRejectForm, setShowLogRejectForm] = useState(false);
  const [showValidateConfirm, setShowValidateConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyNext, setHistoryNext] = useState(null);
  const [historyPrevious, setHistoryPrevious] = useState(null);
  const [centerInfo, setCenterInfo] = useState(null);
  const [showTvaConfig, setShowTvaConfig] = useState(false);
  const [tvaEnabled, setTvaEnabled] = useState(false);
  const [tvaRate, setTvaRate] = useState(20);
  const [activeTab, setActiveTab] = useState('details');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceModalMode, setInvoiceModalMode] = useState('all');
  const [invoiceModalSupplierId, setInvoiceModalSupplierId] = useState(null);
  const [invoiceModalSupplierName, setInvoiceModalSupplierName] = useState('');

  const [supplierDeliveryDates, setSupplierDeliveryDates] = useState({});
  const [deliveryDatesSaving, setDeliveryDatesSaving] = useState(false);

  const [additionalFees, setAdditionalFees] = useState([]);
  const [additionalFeeSaving, setAdditionalFeeSaving] = useState(false);
  const [editingAdditionalFeeId, setEditingAdditionalFeeId] = useState(null);
  const [additionalFeeAction, setAdditionalFeeAction] = useState({ open: false, type: '', feeId: null });
  const [additionalFeeActionNote, setAdditionalFeeActionNote] = useState('');
  const [showAdditionalFeeConsumablesModal, setShowAdditionalFeeConsumablesModal] = useState(false);
  const [consumableFeeDrafts, setConsumableFeeDrafts] = useState([]);
  const additionalFeesRef = useRef(null);
  const [additionalFeeForm, setAdditionalFeeForm] = useState({
    supplier: '',
    label: '',
    amount: '',
    fee_date: '',
    comment: '',
    attachment: null,
  });

  const [validateDeliveryDateAll, setValidateDeliveryDateAll] = useState('');
  const [validateApplyToAll, setValidateApplyToAll] = useState(true);
  const [validateUseFirstDayAll, setValidateUseFirstDayAll] = useState(false);

  const [invoiceWizardIndex, setInvoiceWizardIndex] = useState(0);
  const [invoiceWizardFlowActive, setInvoiceWizardFlowActive] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [documentPreviewId, setDocumentPreviewId] = useState(null);
  const [showStockAdjustmentModal, setShowStockAdjustmentModal] = useState(false);
  const [stockAdjustmentDrafts, setStockAdjustmentDrafts] = useState([]);
  const [stockAdjustmentNote, setStockAdjustmentNote] = useState('');
  const [stockAdjustmentDeliveryDate, setStockAdjustmentDeliveryDate] = useState('');
  const [stockAdjustmentSaving, setStockAdjustmentSaving] = useState(false);
  const [supplierOrdersSyncing, setSupplierOrdersSyncing] = useState(false);

  const getCurrency = () => {
    if (centerInfo) return centerInfo.devise;
    const userCenter = user?.centres_details?.[0];
    if (userCenter) return userCenter.devise;
    return 'EUR';
  };

  const toMoney = (value, currency) => formatMoney(value, { locale: i18n.language, currency: currency || getCurrency() });

  useEffect(() => {
    if (activeTab === 'history' && historyLogs.length === 0) {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const data = await auditLogService.getObjectHistory({
        appLabel: 'sessions_app',
        model: 'sessionformation',
        objectId: id,
        page,
        pageSize: 20,
      });
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setHistoryLogs(results);
      setHistoryNext(data?.next || null);
      setHistoryPrevious(data?.previous || null);
      setHistoryPage(page);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    try {
      return new Date(ts).toLocaleString(i18n.language);
    } catch {
      return ts;
    }
  };

  const invoiceWizardSuppliers = Array.isArray(session?.budget_by_supplier) ? session.budget_by_supplier : [];
  const invoiceWizardTotal = invoiceWizardSuppliers.length;

  const getInvoiceVersionOptions = (supplierId) => {
    if (!supplierId) return [];

    const ordersForSupplier = (Array.isArray(session?.supplier_orders) ? session.supplier_orders : [])
      .filter((order) => (
        String(order?.supplier_id || '') === String(supplierId)
        && order?.status !== 'CANCELLED'
        && Number(order?.version || 1) > 1
      ))
      .sort((a, b) => Number(a.version || 1) - Number(b.version || 1));

    return [
      {
        key: 'original',
        label: t('invoice.modal.originalVersion'),
        order_id: null,
        version: 1,
      },
      ...ordersForSupplier.map((order) => ({
        key: String(order.order_id),
        label: t('invoice.modal.versionNumber', { version: order.version }),
        meta: order.created_at ? formatTimestamp(order.created_at) : '',
        order_id: order.order_id,
        version: order.version,
      })),
    ];
  };

  const startInvoiceWizard = () => {
    if (!invoiceWizardSuppliers.length) return;
    setInvoiceWizardIndex(0);
    const first = invoiceWizardSuppliers[0];
    if (first) {
      setInvoiceWizardFlowActive(true);
      openInvoiceModal('single', first.supplier_id, first.supplier_name);
    }
  };

  const handleWizardSupplierChange = (supplierId, supplierName) => {
    const idx = invoiceWizardSuppliers.findIndex((s) => String(s.supplier_id) === String(supplierId));
    if (idx >= 0) setInvoiceWizardIndex(idx);
    setInvoiceWizardFlowActive(true);
    openInvoiceModal('single', supplierId, supplierName);
  };

  const renderChanges = (log) => {
    const actionDescription = log.action_description;
    if (!actionDescription || actionDescription.length === 0) {
      return null;
    }
    return (
      <div className="history-changes-list">
        {actionDescription.map((change, idx) => (
          <div key={idx} className="history-change-item">
            <span className="history-change-field">{change.field}:</span>
            {change.type === 'update' && (
              <span className="history-change-values">
                <span className="history-old-value">{change.old_value}</span>
                <span className="history-arrow">→</span>
                <span className="history-new-value">{change.new_value}</span>
              </span>
            )}
            {change.type === 'create' && (
              <span className="history-change-values">
                <span className="history-new-value">{change.new_value}</span>
              </span>
            )}
            {change.type === 'delete' && (
              <span className="history-change-values">
                <span className="history-old-value history-deleted">{change.old_value}</span>
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await sessionService.getById(id);
        setSession(response);
        setTvaEnabled(response.tva_enabled || false);
        setTvaRate(response.tva_rate || 20);

        setAdditionalFees(Array.isArray(response?.additional_fees) ? response.additional_fees : []);
        setDocuments(Array.isArray(response?.documents) ? response.documents : []);

        const initial = {};
        (response?.supplier_delivery_dates || []).forEach((row) => {
          if (row?.supplier_id && row?.delivery_date) initial[row.supplier_id] = row.delivery_date;
        });
        setSupplierDeliveryDates(initial);
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [id]);

  useEffect(() => {
    if (location?.hash !== '#additional-fees') return;
    if (!session) return;
    setTimeout(() => {
      try {
        additionalFeesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        // ignore
      }
    }, 50);
  }, [location?.hash, session]);

  const resetAdditionalFeeForm = () => {
    setEditingAdditionalFeeId(null);
    setAdditionalFeeForm({
      supplier: '',
      label: '',
      amount: '',
      fee_date: '',
      comment: '',
      attachment: null,
    });
  };

  const additionalFeeConsumables = Array.isArray(session?.budget_lines)
    ? session.budget_lines.map((ln) => ({
      id: ln.id,
      consommable_name: ln.consommable_name,
      quantity: ln.quantity,
      supplier_id: ln.supplier,
      supplier_name: ln.supplier_name,
      line_total: ln.line_total,
      unit_price: ln.unit_price ?? ((Number(ln.quantity) || 0) ? (Number(ln.line_total) || 0) / Number(ln.quantity) : 0),
    }))
    : [];

  const openConsumableFeeModal = () => {
    const drafts = additionalFeeConsumables.map((row) => ({
      line_id: row.id,
      selected: false,
      quantity: '0',
      comment: '',
    }));
    setConsumableFeeDrafts(drafts);
    setShowAdditionalFeeConsumablesModal(true);
  };

  const closeConsumableFeeModal = () => {
    setShowAdditionalFeeConsumablesModal(false);
    setConsumableFeeDrafts([]);
  };

  const handleCreateAdditionalFeesFromConsumables = async () => {
    const selected = (consumableFeeDrafts || []).filter((d) => d.selected);
    if (!selected.length) {
      toast.error(t('sessionExtra.additionalFees.selectAtLeastOne'));
      return;
    }

    const payloads = [];
    for (const draft of selected) {
      const row = additionalFeeConsumables.find((r) => String(r.id) === String(draft.line_id));
      if (!row) continue;

      const qty = Number(draft.quantity);
      if (!qty || qty <= 0) {
        toast.error(t('sessionExtra.additionalFees.invalidQuantity'));
        return;
      }

      const unit = Number(row.unit_price);
      const amount = Number.isFinite(unit) ? unit * qty : 0;
      payloads.push({
        session: id,
        supplier: row.supplier_id,
        label: row.consommable_name,
        amount: String(amount.toFixed(2)),
        comment: draft.comment || `Consommable: ${row.consommable_name} | Ajout: +${qty}`,
      });
    }

    if (!payloads.length) {
      toast.error(t('sessionExtra.additionalFees.noValidSupply'));
      return;
    }

    try {
      setAdditionalFeeSaving(true);
      let okCount = 0;
      for (const p of payloads) {
        await sessionService.createAdditionalFee(p);
        okCount += 1;
      }

      toast.success(t('sessionExtra.additionalFees.createdCount', { count: okCount }));
      closeConsumableFeeModal();
      await refreshAdditionalFees();
      setTimeout(() => {
        try {
          additionalFeesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) {
          // ignore
        }
      }, 50);
    } catch (e) {
      console.error(e);
      toast.error(t('sessionExtra.additionalFees.createError'));
    } finally {
      setAdditionalFeeSaving(false);
    }
  };

  const refreshAdditionalFees = async () => {
    const updated = await sessionService.getById(id);
    setSession(updated);
    setAdditionalFees(Array.isArray(updated?.additional_fees) ? updated.additional_fees : []);
    setDocuments(Array.isArray(updated?.documents) ? updated.documents : []);
    return updated;
  };

  const refreshDocuments = async () => {
    const updated = await sessionService.getById(id);
    setSession(updated);
    setDocuments(Array.isArray(updated?.documents) ? updated.documents : []);
  };

  const formatFileSize = (size) => {
    const bytes = Number(size || 0);
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const handleUploadDocuments = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setDocumentsUploading(true);
    try {
      for (const file of files) {
        await sessionService.uploadSessionDocument(id, file);
      }
      toast.success(t('sessionExtra.documentsConfirm.uploadedCount', { count: files.length }));
      await refreshDocuments();
    } catch (error) {
      console.error('Error uploading session documents:', error);
      toast.error(t('sessionExtra.documents.uploadError'));
    } finally {
      setDocumentsUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const toastId = toast.custom(
      'danger',
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 700 }}>
          {t('sessionExtra.documentsConfirm.deleteTitle')}
        </div>
        <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          {t('sessionExtra.documentsConfirm.deleteText')}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="toast-btn toast-btn-danger"
            onClick={async () => {
              toast.remove(toastId);
              try {
                await sessionService.deleteSessionDocument(documentId);
                toast.success(t('sessionExtra.documentsConfirm.deleted'));
                await refreshDocuments();
              } catch (error) {
                console.error('Error deleting session document:', error);
                toast.error(t('sessionExtra.documents.deleteError'));
              }
            }}
          >
            {t('common.delete')}
          </button>
          <button
            type="button"
            className="toast-btn"
            onClick={() => toast.remove(toastId)}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>,
      { duration: null }
    );
  };

  useEffect(() => {
    if (!documents.length) {
      setDocumentPreviewId(null);
      return;
    }
    const stillExists = documents.some((document) => String(document.id) === String(documentPreviewId));
    if (!stillExists) setDocumentPreviewId(documents[0].id);
  }, [documents, documentPreviewId]);

  const getDocumentUrl = (document) => document?.file_url || document?.file || '';

  const isImageDocument = (document) => {
    const type = (document?.content_type || '').toLowerCase();
    const name = (document?.original_name || '').toLowerCase();
    return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
  };

  const isPdfDocument = (document) => {
    const type = (document?.content_type || '').toLowerCase();
    const name = (document?.original_name || '').toLowerCase();
    return type.includes('pdf') || name.endsWith('.pdf');
  };

  const renderDocumentPreview = (document) => {
    const url = getDocumentUrl(document);
    if (!document || !url) {
      return (
        <div className="session-document-preview-empty">
          {t('sessionExtra.documentsConfirm.selectForPreview')}
        </div>
      );
    }

    if (isImageDocument(document)) {
      return (
        <div className="session-document-preview-media">
          <img src={url} alt={document.original_name || 'Document'} />
        </div>
      );
    }

    if (isPdfDocument(document)) {
      return (
        <iframe
          className="session-document-preview-frame"
          src={url}
          title={document.original_name || 'Document'}
        />
      );
    }

    return (
      <div className="session-document-preview-empty">
        <FileText size={34} />
        <strong>{t('sessionExtra.documents.previewUnavailable')}</strong>
        <span>{t('sessionExtra.documents.previewUnavailableText')}</span>
      </div>
    );
  };

  const selectedDocument = documents.find((document) => String(document.id) === String(documentPreviewId)) || documents[0] || null;
  const stockMovements = Array.isArray(session?.stock_movements) ? session.stock_movements : [];
  const supplierOrders = Array.isArray(session?.supplier_orders) ? session.supplier_orders : [];
  const canViewSupplierOrders = canView('demandes');
  const canManageSupplierOrders = canEdit('demandes') && !isFournisseur;
  const showSupplierOrdersTab = session?.status === 'VALIDEE' && canViewSupplierOrders && (!isFournisseur || supplierOrders.length > 0);
  const canViewSessionStock = !isFournisseur && canView('stock');
  const canAdjustSessionStock = canEdit('stock') && session?.status === 'VALIDEE';

  const supplierOrderStatusOptions = [
    { value: 'NEW', label: t('demandes.status.NEW') },
    { value: 'ACCEPTED', label: t('demandes.status.ACCEPTED') },
    { value: 'SHIPPED', label: t('demandes.status.SHIPPED') },
    { value: 'RECEIVED', label: t('demandes.status.RECEIVED') },
    { value: 'INVOICED', label: t('demandes.status.INVOICED') },
    { value: 'CANCELLED', label: t('demandes.status.CANCELLED') },
  ];

  const getSupplierOrderStatusLabel = (status) => (
    supplierOrderStatusOptions.find((option) => option.value === status)?.label || status || '-'
  );

  const getSupplierOrderStatusClass = (status) => {
    switch (status) {
      case 'NEW': return 'supplier-order-status-new';
      case 'ACCEPTED': return 'supplier-order-status-accepted';
      case 'SHIPPED': return 'supplier-order-status-shipped';
      case 'RECEIVED': return 'supplier-order-status-received';
      case 'INVOICED': return 'supplier-order-status-invoiced';
      case 'CANCELLED': return 'supplier-order-status-cancelled';
      default: return 'supplier-order-status-new';
    }
  };

  const formatStockDelta = (value) => {
    const qty = Number(value || 0);
    return qty > 0 ? `+${qty}` : String(qty);
  };

  const findExistingSupplierOrderLine = (budgetLine) => {
    const supplierId = String(budgetLine?.supplier || '');
    const consommableId = String(budgetLine?.consommable || '');
    if (!supplierId || !consommableId) return null;

    return supplierOrders.find((order) => (
      String(order?.supplier_id || '') === supplierId
      && order?.status !== 'CANCELLED'
      && Array.isArray(order?.lines)
      && order.lines.some((orderLine) => String(orderLine?.consommable_id || '') === consommableId)
    )) || null;
  };

  const handleUpdateSupplierOrderStatus = async (orderId, newStatus) => {
    if (!orderId || !newStatus) return;

    try {
      await sessionService.updateSupplierOrderStatus(orderId, newStatus);
      await refreshSession();
      toast.success(t('sessionExtra.requests.statusUpdated'));
    } catch (error) {
      console.error('Error updating supplier order status:', error);
      toast.error(error?.response?.data?.detail || t('sessionExtra.requests.statusUpdateError'));
    }
  };

  const handleSyncSupplierOrders = async () => {
    try {
      setSupplierOrdersSyncing(true);
      const response = await sessionService.syncSupplierOrders(id);
      if (response?.session) {
        setSession(response.session);
        setDocuments(Array.isArray(response.session?.documents) ? response.session.documents : []);
        setAdditionalFees(Array.isArray(response.session?.additional_fees) ? response.session.additional_fees : []);
      } else {
        await refreshSession();
      }
      const count = response?.supplier_order_ids?.length || 0;
      toast.success(count ? t('sessionExtra.requests.synced', { count }) : t('sessionExtra.requests.noneMissing'));
    } catch (error) {
      console.error('Error syncing supplier orders:', error);
      toast.error(error?.response?.data?.error || error?.response?.data?.detail || t('sessionExtra.requests.syncError'));
    } finally {
      setSupplierOrdersSyncing(false);
    }
  };

  const openStockAdjustmentModal = () => {
    const drafts = (session?.budget_lines || []).map((line) => ({
      existing_order: findExistingSupplierOrderLine(line),
      id: line.id,
      consommable_name: line.consommable_name,
      supplier_name: line.supplier_name,
      planned_quantity: Number(line.quantity || 0),
      baseline_quantity: Number(line.actual_quantity ?? line.quantity ?? 0),
      actual_quantity: String(line.actual_quantity ?? line.quantity ?? 0),
      adjustment_source: '',
    }));
    setStockAdjustmentDrafts(drafts);
    setStockAdjustmentNote('');
    setStockAdjustmentDeliveryDate('');
    setShowStockAdjustmentModal(true);
  };

  const closeStockAdjustmentModal = () => {
    setShowStockAdjustmentModal(false);
    setStockAdjustmentDrafts([]);
    setStockAdjustmentNote('');
    setStockAdjustmentDeliveryDate('');
  };

  const updateStockAdjustmentDraft = (lineId, value) => {
    const sanitized = Math.max(0, Number(value || 0));
    setStockAdjustmentDrafts((prev) => prev.map((line) => (
      line.id === lineId ? { ...line, actual_quantity: String(sanitized) } : line
    )));
  };

  const updateStockAdjustmentSource = (lineId, value) => {
    setStockAdjustmentDrafts((prev) => prev.map((line) => (
      line.id === lineId ? { ...line, adjustment_source: value } : line
    )));
  };

  const handleSaveStockAdjustment = async () => {
    if (!stockAdjustmentDrafts.length) {
      toast.error(t('sessionExtra.stockAdjustment.noLine'));
      return;
    }

    const missingSource = stockAdjustmentDrafts.find((line) => (
      Number(line.actual_quantity || 0) > Number(line.baseline_quantity || line.planned_quantity || 0)
      && !line.adjustment_source
    ));
    if (missingSource) {
      toast.error(t('sessionExtra.stockAdjustment.missingSource', { name: missingSource.consommable_name }));
      return;
    }

    const payload = {
      note: stockAdjustmentNote,
      delivery_date: stockAdjustmentDeliveryDate || undefined,
      lines: stockAdjustmentDrafts.map((line) => ({
        budget_line: line.id,
        actual_quantity: Number(line.actual_quantity || 0),
        adjustment_source: line.adjustment_source || undefined,
      })),
    };

    try {
      setStockAdjustmentSaving(true);
      const response = await sessionService.adjustStockConsumption(id, payload);
      if (response?.session) {
        setSession(response.session);
        setDocuments(Array.isArray(response.session?.documents) ? response.session.documents : []);
        setAdditionalFees(Array.isArray(response.session?.additional_fees) ? response.session.additional_fees : []);
      } else {
        await refreshSession();
      }
      closeStockAdjustmentModal();
      const orderCount = response?.supplier_order_ids?.length || 0;
      const updatedOrderCount = response?.updated_supplier_order_ids?.length || 0;
      if (orderCount || updatedOrderCount) {
        toast.success(t('sessionExtra.stockAdjustment.savedWithCounts', { orderCount, updatedOrderCount }));
      } else {
        toast.success(t('sessionExtra.stockAdjustment.saved'));
      }
    } catch (error) {
      console.error('Error adjusting stock consumption:', error);
      toast.error(error?.response?.data?.error || t('sessionExtra.stockAdjustment.saveError'));
    } finally {
      setStockAdjustmentSaving(false);
    }
  };

  const handleDownloadDocument = async (document) => {
    if (!document?.id) return;

    try {
      const response = await sessionService.downloadSessionDocument(document.id);
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = document?.original_name || 'document';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading session document:', error);
      toast.error(t('sessionExtra.documents.downloadError'));
    }
  };

  const handleSaveAdditionalFee = async () => {
    if (!additionalFeeForm?.supplier || !additionalFeeForm?.label || !additionalFeeForm?.amount) {
      toast.error(t('sessionExtra.additionalFees.required'));
      return;
    }

    try {
      setAdditionalFeeSaving(true);
      const payload = {
        session: id,
        supplier: additionalFeeForm.supplier,
        label: additionalFeeForm.label,
        amount: additionalFeeForm.amount,
        fee_date: additionalFeeForm.fee_date,
        comment: additionalFeeForm.comment,
      };
      if (additionalFeeForm.attachment) payload.attachment = additionalFeeForm.attachment;

      if (editingAdditionalFeeId) {
        await sessionService.updateAdditionalFee(editingAdditionalFeeId, payload);
        toast.success(t('sessionExtra.additionalFees.updated'));
      } else {
        await sessionService.createAdditionalFee(payload);
        toast.success(t('sessionExtra.additionalFees.created'));
      }

      resetAdditionalFeeForm();
      await refreshAdditionalFees();
    } catch (e) {
      toast.error(t('sessionExtra.additionalFees.saveError'));
    } finally {
      setAdditionalFeeSaving(false);
    }
  };

  const handleEditAdditionalFee = (fee) => {
    if (!fee) return;
    setEditingAdditionalFeeId(fee.id);
    setAdditionalFeeForm({
      supplier: fee.supplier || '',
      label: fee.label || '',
      amount: fee.amount || '',
      fee_date: fee.fee_date || '',
      comment: fee.comment || '',
      attachment: null,
    });
  };

  const handleDeleteAdditionalFee = async (feeId) => {
    if (!feeId) return;
    try {
      await sessionService.deleteAdditionalFee(feeId);
      toast.success(t('sessionExtra.additionalFees.deleted'));
      await refreshAdditionalFees();
    } catch (e) {
      toast.error(t('sessionExtra.additionalFees.deleteError'));
    }
  };

  const openAdditionalFeeAction = (type, feeId) => {
    setAdditionalFeeAction({ open: true, type, feeId });
    setAdditionalFeeActionNote('');
  };

  const closeAdditionalFeeAction = () => {
    setAdditionalFeeAction({ open: false, type: '', feeId: null });
    setAdditionalFeeActionNote('');
  };

  const submitAdditionalFeeAction = async () => {
    const { type, feeId } = additionalFeeAction || {};
    if (!feeId || !type) return;

    try {
      if (type === 'verify') {
        await sessionService.verifyAdditionalFee(feeId, additionalFeeActionNote || '');
        toast.success(t('sessionExtra.additionalFees.verified'));
      } else if (type === 'validate') {
        await sessionService.validateAdditionalFee(feeId, additionalFeeActionNote || '');
        toast.success(t('sessionExtra.additionalFees.validated'));
      } else if (type === 'reject') {
        await sessionService.rejectAdditionalFee(feeId, additionalFeeActionNote || '');
        toast.success(t('sessionExtra.additionalFees.rejected'));
      }
      closeAdditionalFeeAction();
      await refreshAdditionalFees();
    } catch (e) {
      if (type === 'verify') toast.error(t('sessionExtra.additionalFees.verifyError'));
      else if (type === 'validate') toast.error(t('sessionExtra.additionalFees.validateError'));
      else if (type === 'reject') toast.error(t('sessionExtra.additionalFees.rejectError'));
    }
  };

  const handleSaveDeliveryDates = async () => {
    if (!session?.budget_by_supplier?.length) return;
    const deliveryDates = session.budget_by_supplier
      .map((g) => ({ supplier_id: g.supplier_id, delivery_date: supplierDeliveryDates[g.supplier_id] }))
      .filter((row) => row.supplier_id && row.delivery_date);

    try {
      setDeliveryDatesSaving(true);
      await sessionService.setSupplierDeliveryDates(id, deliveryDates);
      const updated = await sessionService.getById(id);
      setSession(updated);

      const next = {};
      (updated?.supplier_delivery_dates || []).forEach((row) => {
        if (row?.supplier_id && row?.delivery_date) next[row.supplier_id] = row.delivery_date;
      });
      setSupplierDeliveryDates(next);
      toast.success(t('sessionExtra.validation.deliveryDatesSaved'));
    } catch (error) {
      toast.error(t('sessions.detail.errors.statusChange'));
    } finally {
      setDeliveryDatesSaving(false);
    }
  };

  useEffect(() => {
    const fetchCenterInfo = async () => {
      if (session?.formation_details?.center_id) {
        try {
          const center = await centresService.getById(session.formation_details.center_id);
          setCenterInfo(center);
        } catch (error) {
          console.error('Error fetching center info:', error);
        }
      }
    };
    fetchCenterInfo();
  }, [session]);

  const openInvoiceModal = (mode, supplierId = null, supplierName = '') => {
    setInvoiceModalMode(mode);
    setInvoiceModalSupplierId(supplierId);
    setInvoiceModalSupplierName(supplierName);
    setShowInvoiceModal(true);
  };

  const handleGenerateInvoice = async (invoiceParams, selectedVersion = null) => {
    try {
      if (invoiceModalMode === 'all') {
        const response = await sessionService.downloadAllInvoices(id, invoiceParams);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Bons_de_commande_Session_${new Date().toISOString().split('T')[0]}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(t('sessions.detail.allInvoicesDownloaded'));
      } else {
        const selectedOrderId = selectedVersion?.order_id || null;
        const response = await sessionService.downloadInvoice(id, invoiceModalSupplierId, invoiceParams, selectedOrderId);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const versionSuffix = selectedVersion?.version && Number(selectedVersion.version) > 1 ? `_V${selectedVersion.version}` : '';
        link.setAttribute('download', `Bon_de_commande_${invoiceModalSupplierName.replace(/\s+/g, '_')}${versionSuffix}_${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(t('sessions.detail.invoiceDownloaded', { supplier: invoiceModalSupplierName }));
      }

      if (isAdmin && invoiceWizardFlowActive && invoiceModalMode === 'single') {
        setInvoiceWizardIndex((v) => {
          const next = Math.min(invoiceWizardTotal - 1, v + 1);
          const nextSupplier = invoiceWizardSuppliers[next];
          if (nextSupplier) {
            setInvoiceModalSupplierId(nextSupplier.supplier_id);
            setInvoiceModalSupplierName(nextSupplier.supplier_name);
          }
          return next;
        });
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(invoiceModalMode === 'all'
        ? t('sessions.detail.errors.downloadInvoices')
        : t('sessions.detail.errors.downloadInvoice'));
      throw error;
    }
  };

  const handleUpdateTva = async () => {
    try {
      await sessionService.update(id, { tva_enabled: tvaEnabled, tva_rate: parseFloat(tvaRate) });
      const updated = await sessionService.getById(id);
      setSession(updated);
      setShowTvaConfig(false);
      toast.success(t('sessions.detail.tvaUpdated'));
    } catch (error) {
      console.error('Error updating TVA:', error);
      toast.error(t('sessions.detail.errors.updateTva'));
    }
  };

  const applyAllDeliveryDate = (dateValue) => {
    if (!dateValue || !session?.budget_by_supplier?.length) return;
    setSupplierDeliveryDates((prev) => {
      const next = { ...prev };
      session.budget_by_supplier.forEach((g) => {
        next[g.supplier_id] = dateValue;
      });
      return next;
    });
  };

  useEffect(() => {
    if (!showValidateConfirm) return;
    const firstDay = session?.start_date || '';
    setValidateApplyToAll(true);
    setValidateUseFirstDayAll(Boolean(firstDay));
    setValidateDeliveryDateAll(firstDay);
    if (firstDay) applyAllDeliveryDate(firstDay);
  }, [showValidateConfirm]);

  const handleValidate = async () => {
    try {
      if ((isAdmin || isManager) && session?.budget_by_supplier?.length) {
        const supplierIds = session.budget_by_supplier.map((g) => g.supplier_id);
        const missing = supplierIds.filter((sid) => !supplierDeliveryDates[sid]);
        if (missing.length) {
          toast.error('Veuillez renseigner une date de livraison pour chaque fournisseur avant validation');
          return;
        }

        setDeliveryDatesSaving(true);
        await sessionService.setSupplierDeliveryDates(
          id,
          supplierIds.map((sid) => ({ supplier_id: sid, delivery_date: supplierDeliveryDates[sid] }))
        );
      }
      await sessionService.validate(id);
      setShowValidateConfirm(false);
      const updated = await sessionService.getById(id);
      setSession(updated);
    } catch (error) {
      const apiError = error?.response?.data;
      if (apiError?.missing_supplier_ids?.length) {
        toast.error('Veuillez renseigner une date de livraison pour chaque fournisseur avant validation');
      } else {
        toast.error(t('sessions.detail.errors.validate'));
      }
    } finally {
      setDeliveryDatesSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      switch (newStatus) {
        case 'EN_ATTENTE':
          await sessionService.reset(id);
          break;
        case 'VALIDEE':
          await sessionService.validate(id);
          break;
        case 'REFUSEE':
          if (!rejectReason.trim()) {
            setShowRejectForm(true);
            return;
          }
          await sessionService.reject(id, rejectReason);
          setRejectReason('');
          break;
        default:
          return;
      }
      const updated = await sessionService.getById(id);
      setSession(updated);
      setShowValidateConfirm(false);
      setShowResetConfirm(false);
    } catch (error) {
      const apiError = error?.response?.data;
      if (apiError?.missing_supplier_ids?.length) {
        toast.error('Veuillez renseigner une date de livraison pour chaque fournisseur avant validation');
      } else {
        toast.error(t('sessions.detail.errors.statusChange'));
      }
    }
  };

  const handleReject = async () => {
    try {
      await sessionService.reject(id, rejectReason);
      const updated = await sessionService.getById(id);
      setSession(updated);
      setShowRejectForm(false);
      setRejectReason('');
    } catch (error) {
      toast.error(t('sessions.detail.errors.reject'));
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'VERIFICATION': return 'status-pending';
      case 'DEMANDE_MODIF': return 'status-pending';
      case 'DEMANDE_MODIF_MANAGER': return 'status-pending';
      case 'EN_ATTENTE': return 'status-pending';
      case 'VERIFIE': return 'status-approved';
      case 'VALIDEE': return 'status-validated';
      case 'VALIDE': return 'status-validated';
      case 'REFUSEE': return 'status-rejected';
      case 'REFUSE': return 'status-rejected';
      case 'ANNULEE': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  const refreshSession = async () => {
    const updated = await sessionService.getById(id);
    setSession(updated);
    return updated;
  };

  const handleLogMarkVerified = async () => {
    try {
      await sessionService.markVerified(id);
      await refreshSession();
      setShowLogNoteForm(false);
      setShowLogRejectForm(false);
      setLogNote('');
      setLogRejectReason('');
      toast.success(t('status.session.VERIFIE'));
    } catch (error) {
      toast.error(t('sessions.detail.errors.statusChange'));
    }
  };

  const handleLogRequestModification = async () => {
    if (!logNote.trim()) return;
    try {
      await sessionService.requestModification(id, logNote.trim());
      await refreshSession();
      setShowLogNoteForm(false);
      setLogNote('');
      toast.success(t('status.session.DEMANDE_MODIF'));
    } catch (error) {
      toast.error(t('sessions.detail.errors.statusChange'));
    }
  };

  const handleLogReject = async () => {
    try {
      await sessionService.rejectVerification(id, logRejectReason.trim());
      await refreshSession();
      setShowLogRejectForm(false);
      setLogRejectReason('');
      toast.success(t('status.session.REFUSEE'));
    } catch (error) {
      toast.error(t('sessions.detail.errors.statusChange'));
    }
  };

  if (loading) {
    return <div className="page-loading">{t('common.loading')}</div>;
  }

  if (!session) {
    return <div className="empty-state">{t('sessions.detail.notFound')}</div>;
  }

  return (
    <div className="session-detail-page">
      <Link to="/sessions" className="back-link">
        ← {t('sessions.detail.back')}
      </Link>


      <div className="session-content-layout">
        {/* Information card */}
        <div className="session-info-card">
          <span className={`status-badge-large ${getStatusClass(session.status)}`}>
            {t(`status.session.${session.status}`)}
          </span>
          <h1>{session.formation_details?.title}</h1>
          <div className="session-info-item">
            <span className="session-info-label">{t('sessions.detail.trainer')}</span>
            <span className="session-info-value">{session.trainer_name}</span>
          </div>
          {(session.instructor_details?.full_name || session.instructor_name) && (
            <div className="session-info-item">
              <span className="session-info-label">{t('sessions.detail.instructor')}</span>
              <span className="session-info-value">{session.instructor_details?.full_name || session.instructor_name}</span>
            </div>
          )}
          <div className="session-info-item">
            <span className="session-info-label">{t('sessions.detail.students')}</span>
            <span className="session-info-value">{t('common.studentsCount', { count: session.student_count })}</span>
          </div>
          <div className="session-info-item">
            <span className="session-info-label">{t('sessions.detail.trainers')}</span>
            <span className="session-info-value">{t('common.trainersCount', { count: session.trainer_count || 1 })}</span>
          </div>
          <div className="session-info-row session-info-row-two">
            <div className="session-info-item">
              <span className="session-info-label">{t('sessions.detail.visitors')}</span>
              <span className="session-info-value">{session.visitor_count || 0}</span>
            </div>
            <div className="session-info-item">
              <span className="session-info-label">{t('sessions.detail.visitorDays')}</span>
              <span className="session-info-value">{session.visitor_days || 0}</span>
            </div>
          </div>
          <div className="session-info-item">
            <span className="session-info-label">{t('sessions.detail.staffCount')}</span>
            <span className="session-info-value">{session.staff_count || 0}</span>
          </div>
          <div className="session-info-item">
            <span className="session-info-label">Stock</span>
            <span className="session-info-value">
              {session.consume_stock ? t('sessionExtra.stock.consumptionEnabled') : t('sessionExtra.stock.notConsumed')}
            </span>
            {session.stock_consumed_at && (
              <span className="session-info-note">
                {t('sessionExtra.stock.consumedOn', { date: new Date(session.stock_consumed_at).toLocaleDateString(i18n.language) })}
              </span>
            )}
            {session.stock_adjusted_at && (
              <span className="session-info-note">
                {t('sessionExtra.stock.adjustedOn', { date: new Date(session.stock_adjusted_at).toLocaleDateString(i18n.language) })}
              </span>
            )}
          </div>
          <div className="session-info-item">
            <span className="session-info-label">{t('sessions.detail.createdAt')}</span>
            <span className="session-info-value">{new Date(session.created_at).toLocaleDateString(i18n.language)}</span>
          </div>
          {(isResponsable || isAdmin) && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={openConsumableFeeModal}
                disabled={additionalFeeSaving}
              >
                {t('sessionExtra.additionalFees.button')}
              </button>
            </div>
          )}
          {session.status === 'VALIDEE' && (
            <div className="session-info-item">
              <span className="session-info-label">{t('sessions.detail.validatedAt')}</span>
              <span className="session-info-value">{new Date(session.validated_at).toLocaleDateString(i18n.language)} {t('common.by')} {session.validated_by_name}</span>
            </div>
          )}
          {session.status === 'REFUSEE' && session.rejection_reason && (
            <div className="session-info-item">
              <span className="session-info-label">{t('sessions.detail.rejectReason')}</span>
              <p className="rejection-reason">{session.rejection_reason}</p>
            </div>
          )}
          {session.status === 'DEMANDE_MODIF' && session.rejection_reason && (
            <div className="session-info-item">
              <span className="session-info-label">Note</span>
              <p className="rejection-reason">{session.rejection_reason}</p>
            </div>
          )}
          {session.status === 'DEMANDE_MODIF_MANAGER' && session.rejection_reason && (
            <div className="session-info-item">
              <span className="session-info-label">Note (Manager)</span>
              <p className="rejection-reason">{session.rejection_reason}</p>
            </div>
          )}
          {(isAdmin || (canDownload('sessions') && !isFournisseur)) && (
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={startInvoiceWizard}
              style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              <FileText size={18} /> {t('sessions.detail.downloadAllInvoices')}
            </button>
          )}
          <ConsolidatedOrderDownloadButton
            sessionId={id}
            isAdmin={isAdmin}
            isFournisseur={isFournisseur}
            hasDownloadPermission={canDownload('sessions')}
          />
          {isFournisseur && canDownload('sessions') && (
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={() => {
                const group = session.budget_by_supplier?.find(g => g.supplier_id === user.fournisseur);
                if (group) {
                  openInvoiceModal('single', group.supplier_id, group.supplier_name);
                }
              }}
              style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              <FileText size={18} /> {t('sessions.detail.downloadSupplierInvoice')}
            </button>
          )}
        </div>

        {/* Main content */}
        <div>
          {/* Grand badge budget vert */}
          <div className="budget-hero-card">
          <div className="budget-hero-label">{t('sessions.detail.totalBudget')}</div>
          <div className="budget-hero-amount">
            {session.budget_totals_by_currency && Object.keys(session.budget_totals_by_currency).length > 0 ? (
              Object.entries(session.budget_totals_by_currency).map(([currency, amount], index, arr) => (
                <span key={currency} className="amount">
                  {toMoney(amount, currency)}
                  {index < arr.length - 1 && <span className="currency-separator"> / </span>}
                </span>
              ))
            ) : (
              <span className="amount">{session.total_budget}</span>
            )}
          </div>
          {session.tva_enabled && (
            <div className="budget-hero-tva">TVA {parseFloat(session.tva_rate).toFixed(1)}% comprise</div>
          )}
        </div>

        {/* Commentaire global du responsable */}
        {session.comment && (
          <div style={{ 
            marginBottom: '1.5rem', 
            padding: '1rem', 
            background: '#f0f9ff', 
            borderRadius: '8px', 
            border: '1px solid #bae6fd' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#0369a1' }}>
                Commentaire global du responsable
              </h3>
              {(isResponsable || isAdmin) && (
                <button 
                  type="button" 
                  className="btn btn-sm btn-secondary"
                  onClick={() => navigate(`/sessions/${id}/edit`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Pencil size={14} /> Modifier
                </button>
              )}
            </div>
            <p style={{ margin: 0, color: '#374151', whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
              {session.comment}
            </p>
          </div>
        )}

        {(isResponsable || isAdmin) && (
          <div style={{ marginBottom: '1.5rem', textAlign: 'right' }}>
            <button 
              type="button" 
              className="btn btn-sm btn-secondary"
              onClick={() => setShowTvaConfig(!showTvaConfig)}
            >
              {t('sessions.detail.configureTva')}
            </button>
          </div>
        )}

        {showTvaConfig && (isResponsable || isAdmin) && (
          <div className="tva-config-box" style={{ 
            background: '#f0fdf4', 
            border: '1px solid #10b981', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1rem' 
          }}>
            <h3 style={{ marginTop: 0, color: '#059669' }}>{t('sessions.detail.tvaConfiguration')}</h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={tvaEnabled}
                  onChange={(e) => setTvaEnabled(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>{t('sessions.detail.enableTva')}</span>
              </label>
            </div>
            {tvaEnabled && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  {t('sessions.detail.tvaRate')}
                </label>
                <input 
                  type="number" 
                  value={tvaRate}
                  onChange={(e) => setTvaRate(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  style={{ 
                    padding: '0.5rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '4px',
                    width: '120px'
                  }}
                /> %
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleUpdateTva}
              >
                {t('common.save')}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowTvaConfig(false);
                  setTvaEnabled(session.tva_enabled || false);
                  setTvaRate(session.tva_rate || 20);
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Onglets */}
        <div className="session-tabs">
          <button 
            className={`session-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            {t('sessions.detail.tabs.details')}
          </button>
          {!isFournisseur && (
            <button
              className={`session-tab ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              {t('sessionExtra.documents.tab')}{documents.length ? ` (${documents.length})` : ''}
            </button>
          )}
          {canViewSessionStock && (
            <button
              className={`session-tab ${activeTab === 'stock' ? 'active' : ''}`}
              onClick={() => setActiveTab('stock')}
            >
              {t('sessionExtra.stock.tab')}{stockMovements.length ? ` (${stockMovements.length})` : ''}
            </button>
          )}
          {showSupplierOrdersTab && (
            <button
              className={`session-tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              {t('sessionExtra.requests.tab')} ({supplierOrders.length})
            </button>
          )}
          <button 
            className={`session-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            {t('sessions.detail.tabs.history')}
          </button>
        </div>

        {/* Details tab content */}
        {activeTab === 'details' && (
          <>
            {showAdditionalFeeConsumablesModal && !isFournisseur && (
              <div className="dialog-overlay ui-dialog-overlay" onClick={(e) => {
                if (e.target === e.currentTarget) closeConsumableFeeModal();
              }}>
                <div className="dialog-container ui-dialog-container additional-fees-consumable-dialog" style={{ maxWidth: '900px' }}>
                  <div className="dialog-header ui-dialog-header">
                    <div className="dialog-title ui-dialog-title">
                      <h2 style={{ margin: 0 }}>{t('sessionExtra.additionalFees.title')}</h2>
                    </div>
                    <button type="button" className="dialog-close" onClick={closeConsumableFeeModal}>
                      {t('common.close', { defaultValue: 'Fermer' })}
                    </button>
                  </div>

                  <div className="dialog-form">
                    <p style={{ marginTop: 0 }}>{t('sessionExtra.additionalFees.helper')}</p>

                    <div className="ui-fixed-list additional-fee-consumables-list">
                      <div className="ui-fixed-list-scroll">
                        <div className="table-container">
                          <table className="data-table additional-fee-consumables-table">
                            <thead>
                              <tr>
                                <th style={{ width: '90px' }}>{t('sessionExtra.additionalFees.add')}</th>
                                <th>{t('sessionExtra.additionalFees.product')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {additionalFeeConsumables.length === 0 ? (
                                <tr>
                                  <td colSpan={2} style={{ color: '#6b7280' }}>{t('sessionExtra.additionalFees.emptySupply')}</td>
                                </tr>
                              ) : (
                                additionalFeeConsumables.map((row) => {
                                  const draft = (consumableFeeDrafts || []).find((d) => String(d.line_id) === String(row.id));
                                  const isSelected = !!draft?.selected;
                                  const quantityValue = Math.max(0, Number(draft?.quantity || 0));
                                  return (
                                    <tr
                                      key={row.id}
                                      style={{ background: isSelected ? 'rgba(118, 184, 42, 0.08)' : undefined }}
                                    >
                                      <td className="consumable-add-cell">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setConsumableFeeDrafts((prev) => (prev || []).map((d) => (
                                              String(d.line_id) === String(row.id) ? { ...d, selected: checked } : d
                                            )));
                                          }}
                                        />
                                      </td>
                                      <td className="consumable-product-cell">
                                        <div className="consumable-product-name">{row.consommable_name}</div>
                                        <div className="consumable-ordered-line">
                                          <span className="consumable-quantity-label">{t('sessionExtra.additionalFees.alreadyOrdered')}</span>
                                          <span className="consumable-quantity-value">{row.quantity}</span>
                                        </div>
                                        <div className="consumable-add-line">
                                          <label className="consumable-add-label" htmlFor={`add-qty-${row.id}`}>{t('sessionExtra.additionalFees.toAdd')}</label>
                                          <div className="consumable-qty-control">
                                            <button
                                              type="button"
                                              className="qty-arrow-btn"
                                              onClick={() => {
                                                if (!isSelected) return;
                                                setConsumableFeeDrafts((prev) => (prev || []).map((d) => (
                                                  String(d.line_id) === String(row.id)
                                                    ? { ...d, quantity: String(Math.max(0, (Number(d.quantity || 0) - 1))) }
                                                    : d
                                                )));
                                              }}
                                              disabled={!isSelected || quantityValue <= 0}
                                              aria-label={t('sessionExtra.additionalFees.decreaseQuantity')}
                                            >
                                              -
                                            </button>
                                            <input
                                              id={`add-qty-${row.id}`}
                                              type="number"
                                              min="0"
                                              step="1"
                                              value={draft?.quantity ?? ''}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '') {
                                                  setConsumableFeeDrafts((prev) => (prev || []).map((d) => (
                                                    String(d.line_id) === String(row.id) ? { ...d, quantity: '' } : d
                                                  )));
                                                  return;
                                                }
                                                const numericValue = Math.max(0, Number(val));
                                                setConsumableFeeDrafts((prev) => (prev || []).map((d) => (
                                                  String(d.line_id) === String(row.id) ? { ...d, quantity: String(numericValue) } : d
                                                )));
                                              }}
                                              disabled={!isSelected}
                                              className="ui-input consumable-add-input"
                                              style={{ padding: '0.5rem 0.75rem' }}
                                            />
                                            <button
                                              type="button"
                                              className="qty-arrow-btn"
                                              onClick={() => {
                                                if (!isSelected) return;
                                                setConsumableFeeDrafts((prev) => (prev || []).map((d) => (
                                                  String(d.line_id) === String(row.id)
                                                    ? { ...d, quantity: String(Number(d.quantity || 0) + 1) }
                                                    : d
                                                )));
                                              }}
                                              disabled={!isSelected}
                                              aria-label={t('sessionExtra.additionalFees.increaseQuantity')}
                                            >
                                              +
                                            </button>
                                          </div>
                                        </div>
                                        <div className="consumable-comment-line">
                                          <label className="consumable-add-label" htmlFor={`add-comment-${row.id}`}>{t('sessionExtra.additionalFees.comment')}</label>
                                          <input
                                            id={`add-comment-${row.id}`}
                                            type="text"
                                            value={draft?.comment ?? ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setConsumableFeeDrafts((prev) => (prev || []).map((d) => (
                                                String(d.line_id) === String(row.id) ? { ...d, comment: val } : d
                                              )));
                                            }}
                                            disabled={!isSelected}
                                            className="ui-input"
                                            style={{ padding: '0.5rem 0.75rem' }}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="ui-fixed-list-footer" style={{ paddingTop: '0.75rem' }}>
                        <div className="dialog-actions ui-dialog-actions">
                          <button
                            type="button"
                            onClick={closeConsumableFeeModal}
                            className="btn-secondary ui-btn-secondary"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            type="button"
                            onClick={handleCreateAdditionalFeesFromConsumables}
                            className="btn-primary ui-btn-primary"
                            disabled={additionalFeeSaving}
                          >
                            {t('common.confirm')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Liste des articles par fournisseur */}
            {session.budget_by_supplier?.filter(group => !isFournisseur || group.supplier_id === user.fournisseur).map((group) => (
              <div key={group.supplier_id} className="supplier-budget-section" style={{ marginBottom: '2rem' }}>
                <div className="supplier-budget-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 className="supplier-budget-title" style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: 0 }}>{group.supplier_name}</h3>
                  <div className="supplier-budget-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {(isManager || isAdmin) && (
                      <>
                        <input
                          className="supplier-date-input"
                          type="date"
                          value={supplierDeliveryDates[group.supplier_id] || ''}
                          onChange={(e) => setSupplierDeliveryDates((prev) => ({ ...prev, [group.supplier_id]: e.target.value }))}
                          style={{
                            padding: '0.35rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={handleSaveDeliveryDates}
                          disabled={deliveryDatesSaving}
                        >
                          {deliveryDatesSaving ? t('common.saving') : t('common.save')}
                        </button>
                      </>
                    )}

                    {(isAdmin || (
                      canDownload('sessions')
                      && (!isFournisseur || group.supplier_id === user.fournisseur)
                    )) && (
                      <button 
                        type="button" 
                        className="btn btn-sm btn-secondary"
                        onClick={() => openInvoiceModal('single', group.supplier_id, group.supplier_name)}
                        title={t('sessions.detail.downloadSupplierInvoice')}
                      >
                        <FileText size={14} /> PDF
                      </button>
                    )}
                  </div>
            </div>
            
            <div className="items-list">
              {group.lines.map((line, index) => {
                const plannedQuantity = Number(line.quantity || 0);
                const hasActualQuantity = line.actual_quantity !== null && line.actual_quantity !== undefined;
                const actualQuantity = hasActualQuantity ? Number(line.actual_quantity || 0) : plannedQuantity;
                const quantityDelta = actualQuantity - plannedQuantity;
                return (
                <div key={index} className="item-row" style={{ 
                  flexDirection: 'column', 
                  alignItems: 'stretch',
                  background: line.comment ? '#fffbeb' : 'white',
                  border: line.comment ? '2px solid #fbbf24' : '1px solid #e5e7eb',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="item-row-main" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="item-info" style={{ flex: 1 }}>
                      <div className="item-name" style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                        {line.consommable_name}
                      </div>
                      <div className="item-details" style={{ 
                        fontSize: '0.875rem', 
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ 
                          background: line.comment ? '#fef3c7' : '#f3f4f6',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontWeight: 600,
                          color: line.comment ? '#92400e' : '#374151'
                        }}>
                          {hasActualQuantity ? t('sessionExtra.budget.actualUnits', { count: actualQuantity }) : t('sessionExtra.budget.units', { count: plannedQuantity })}
                        </span>
                        {hasActualQuantity && (
                          <span style={{
                            background: quantityDelta === 0 ? '#f3f4f6' : quantityDelta > 0 ? '#fee2e2' : '#dcfce7',
                            color: quantityDelta === 0 ? '#374151' : quantityDelta > 0 ? '#b91c1c' : '#047857',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}>
                            {t('sessionExtra.budget.plannedDelta', { planned: plannedQuantity, delta: `${quantityDelta >= 0 ? '+' : ''}${quantityDelta}` })}
                          </span>
                        )}
                        {line.comment && (
                          <span style={{ 
                            fontSize: '0.75rem',
                            color: '#92400e',
                            fontWeight: 600,
                            background: '#fef3c7',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px'
                          }}>
                            {t('sessionExtra.budget.modified')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="item-row-total" style={{ textAlign: 'right' }}>
                      <div className="item-price" style={{ fontSize: '1.125rem', fontWeight: 700, color: '#059669' }}>
                        {toMoney(line.line_total, line.effective_devise || line.devise)}
                      </div>
                      <div className="item-calculation" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {line.calculation_detail && <div>{line.calculation_detail}</div>}
                        {actualQuantity} × {toMoney(line.unit_price, line.effective_devise || line.devise)}
                      </div>
                    </div>
                  </div>
                  {line.comment && (
                    <div style={{ 
                      marginTop: '0.75rem', 
                      padding: '0.75rem', 
                      background: '#fef3c7', 
                      borderRadius: '6px', 
                      border: '1px solid #fbbf24',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{ 
                        fontWeight: 600, 
                        color: '#92400e', 
                        marginBottom: '0.5rem', 
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>💬</span>
                        <span>{t('sessionExtra.additionalFees.modificationJustification')}</span>
                      </div>
                      <div style={{ color: '#78350f', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                        {line.comment}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
            ))}

            {/* Frais additionnels */}
            {!isFournisseur && (
              <div
                id="additional-fees"
                className="additional-fees-section"
                ref={additionalFeesRef}
                style={{ marginTop: '2rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '10px', background: 'white' }}
              >
                <div className="additional-fees-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0 }}>{t('sessionExtra.additionalFees.pluralTitle')}</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {(additionalFees || []).length === 0 && (
                    <div style={{ color: '#6b7280' }}>{t('sessionExtra.additionalFees.empty')}</div>
                  )}

                  {additionalFeeAction?.open && (
                    <div className="reject-form" style={{ marginBottom: '0.75rem' }}>
                      <label htmlFor="fee_action_note">
                        {additionalFeeAction.type === 'verify'
                          ? t('sessionExtra.additionalFees.verificationNote')
                          : additionalFeeAction.type === 'validate'
                            ? t('sessionExtra.additionalFees.validationNote')
                            : t('sessionExtra.additionalFees.rejectNote')}
                      </label>
                      <textarea
                        id="fee_action_note"
                        value={additionalFeeActionNote}
                        onChange={(e) => setAdditionalFeeActionNote(e.target.value)}
                        rows="3"
                        className="form-input"
                        placeholder={t('sessionExtra.additionalFees.notePlaceholder')}
                      />
                      <div className="reject-actions">
                        <button
                          type="button"
                          onClick={submitAdditionalFeeAction}
                          className={`btn additional-fee-action-confirm ${
                            additionalFeeAction.type === 'reject'
                              ? 'btn-danger additional-fee-action-confirm-reject'
                              : additionalFeeAction.type === 'validate'
                                ? 'btn-success additional-fee-action-confirm-validate'
                                : 'btn-warning additional-fee-action-confirm-verify'
                          }`}
                        >
                          {t('common.confirm')}
                        </button>
                        <button
                          type="button"
                          onClick={closeAdditionalFeeAction}
                          className="btn btn-secondary additional-fee-action-cancel"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}

                  {(additionalFees || []).map((fee) => {
                    const canDelete = (isResponsable || isAdmin) && fee.status === 'EN_ATTENTE';
                    const canVerify = (isLogistiqueCoordinator || isAdmin) && fee.status === 'EN_ATTENTE';
                    const canValidate = (isManager || isAdmin) && fee.status === 'VERIFIE';
                    const canReject = (isManager || isAdmin) && (fee.status === 'EN_ATTENTE' || fee.status === 'VERIFIE');

                    return (
                      <div
                        key={fee.id}
                        className="additional-fee-card"
                        style={{
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          background: fee.status === 'REFUSE' ? '#fef2f2' : fee.status === 'VALIDE' ? '#f0fdf4' : 'white',
                        }}
                      >
                        <div className="additional-fee-main" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                          <div className="additional-fee-info" style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div style={{ fontWeight: 700 }}>{fee.label}</div>
                              <span className={`status-badge ${getStatusClass(fee.status)}`}>{fee.status_display || fee.status}</span>
                            </div>
                            <div style={{ marginTop: '0.25rem', color: '#374151', fontSize: '0.875rem' }}>
                              <div><b>{t('sessionExtra.additionalFees.supplier')}:</b> {fee.supplier_name || '-'}</div>
                              <div><b>{t('sessionExtra.additionalFees.date')}:</b> {fee.fee_date || '-'}</div>
                              <div><b>{t('sessionExtra.additionalFees.amount')}:</b> {toMoney(fee.amount)}</div>
                              {fee.comment && (<div style={{ marginTop: '0.25rem' }}><b>{t('sessionExtra.additionalFees.comment')}:</b> {fee.comment}</div>)}
                              {fee.attachment && (
                                <div style={{ marginTop: '0.25rem' }}>
                                  <a href={fee.attachment} target="_blank" rel="noreferrer">{t('sessionExtra.additionalFees.attachment')}</a>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="additional-fee-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {canDelete && (
                              <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDeleteAdditionalFee(fee.id)}>
                                {t('common.delete')}
                              </button>
                            )}
                            {canVerify && (
                              <button type="button" className="btn btn-sm btn-warning" onClick={() => openAdditionalFeeAction('verify', fee.id)}>
                                {t('sessionExtra.additionalFees.verify')}
                              </button>
                            )}
                            {canValidate && (
                              <button type="button" className="btn btn-sm btn-success" onClick={() => openAdditionalFeeAction('validate', fee.id)}>
                                {t('sessionExtra.additionalFees.validate')}
                              </button>
                            )}
                            {canReject && (
                              <button type="button" className="btn btn-sm btn-danger" onClick={() => openAdditionalFeeAction('reject', fee.id)}>
                                {t('sessionExtra.additionalFees.reject')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </>
          )}

          {showStockAdjustmentModal && !isFournisseur && (
            <div className="dialog-overlay ui-dialog-overlay" onClick={(event) => {
              if (event.target === event.currentTarget) closeStockAdjustmentModal();
            }}>
              <div className="dialog-container ui-dialog-container stock-adjustment-dialog">
                <div className="dialog-header ui-dialog-header">
                  <div className="dialog-title ui-dialog-title">
                    <h2 style={{ margin: 0 }}>
                      {session.stock_adjusted_at ? t('sessionExtra.stockAdjustment.readjustTitle') : t('sessionExtra.stockAdjustment.title')}
                    </h2>
                  </div>
                  <button type="button" className="dialog-close" onClick={closeStockAdjustmentModal}>
                    {t('common.close', { defaultValue: 'Fermer' })}
                  </button>
                </div>

                <div className="dialog-form">
                  <div className="stock-adjustment-list">
                    <div className="stock-adjustment-scroll">
                      <table className="data-table stock-adjustment-table">
                        <thead>
                          <tr>
                            <th>{t('sessionExtra.stockAdjustment.consommable')}</th>
                            <th>{t('sessionExtra.stockAdjustment.supplier')}</th>
                            <th>{t('sessionExtra.stockAdjustment.planned')}</th>
                            <th>{t('sessionExtra.stockAdjustment.actual')}</th>
                            <th>{t('sessionExtra.stockAdjustment.delta')}</th>
                            <th>{t('sessionExtra.stockAdjustment.source')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockAdjustmentDrafts.map((line) => {
                            const planned = Number(line.planned_quantity || 0);
                            const baseline = Number(line.baseline_quantity || planned);
                            const actual = Number(line.actual_quantity || 0);
                            const delta = actual - planned;
                            const adjustmentDelta = actual - baseline;
                            return (
                              <tr key={line.id}>
                                <td>{line.consommable_name}</td>
                                <td>{line.supplier_name || '-'}</td>
                                <td>{planned}</td>
                                <td>
                                  <div className="stock-adjustment-qty-control">
                                    <button
                                      type="button"
                                      className="qty-arrow-btn"
                                      onClick={() => updateStockAdjustmentDraft(line.id, actual - 1)}
                                      disabled={actual <= 0}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      min="0"
                                      className="form-input"
                                      value={line.actual_quantity}
                                      onChange={(event) => updateStockAdjustmentDraft(line.id, event.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="qty-arrow-btn"
                                      onClick={() => updateStockAdjustmentDraft(line.id, actual + 1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <span className={`stock-delta ${delta >= 0 ? 'stock-delta-positive' : 'stock-delta-negative'}`}>
                                    {formatStockDelta(delta)}
                                  </span>
                                </td>
                                <td>
                                  {adjustmentDelta > 0 ? (
                                    <div className="stock-adjustment-source">
                                      <label className={!line.existing_order ? 'stock-adjustment-source-disabled' : undefined}>
                                        <input
                                          type="radio"
                                          name={`adjustment-source-${line.id}`}
                                          checked={line.adjustment_source === 'STOCK'}
                                          onChange={() => updateStockAdjustmentSource(line.id, 'STOCK')}
                                        />
                                        {t('sessionExtra.stockAdjustment.useStock')}
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`adjustment-source-${line.id}`}
                                          checked={line.adjustment_source === 'ORDER_RECEIVED'}
                                          disabled={!line.existing_order}
                                          onChange={() => updateStockAdjustmentSource(line.id, 'ORDER_RECEIVED')}
                                        />
                                        {t('sessionExtra.stockAdjustment.alreadyOrdered')}
                                      </label>
                                      {!line.existing_order && (
                                        <span className="stock-adjustment-source-hint">
                                          {t('sessionExtra.stockAdjustment.noExistingOrder')}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="stock-adjustment-source-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="stock-adjustment-extra">
                    <label>
                      {t('sessionExtra.stockAdjustment.deliveryDate')}
                      <input
                        type="date"
                        className="form-input"
                        value={stockAdjustmentDeliveryDate}
                        onChange={(event) => setStockAdjustmentDeliveryDate(event.target.value)}
                      />
                    </label>
                    <label>
                      {t('sessionExtra.stockAdjustment.note')}
                      <textarea
                        className="form-input"
                        rows="3"
                        value={stockAdjustmentNote}
                        onChange={(event) => setStockAdjustmentNote(event.target.value)}
                        placeholder={t('sessionExtra.stockAdjustment.notePlaceholder')}
                      />
                    </label>
                  </div>

                  <div className="dialog-actions ui-dialog-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeStockAdjustmentModal} disabled={stockAdjustmentSaving}>
                      {t('common.cancel')}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSaveStockAdjustment} disabled={stockAdjustmentSaving}>
                      {stockAdjustmentSaving ? t('common.saving') : (session.stock_adjusted_at ? t('sessionExtra.stockAdjustment.resave') : t('sessionExtra.stockAdjustment.save'))}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contenu de l'onglet Stock */}
          {canViewSessionStock && activeTab === 'stock' && (
            <div className="stock-movements-tab-content">
              <div className="stock-movements-header">
                <div>
                  <h2>{t('sessionExtra.stock.title')}</h2>
                  <p>{t('sessionExtra.stock.subtitle')}</p>
                </div>
                <div className="stock-movements-header-actions">
                  <span className={`stock-consumption-badge ${session.consume_stock ? 'stock-consumption-enabled' : ''}`}>
                    {session.consume_stock ? t('sessionExtra.stock.enabled') : t('sessionExtra.stock.disabled')}
                  </span>
                  {canAdjustSessionStock && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={openStockAdjustmentModal}
                    >
                      {session.stock_adjusted_at ? t('sessionExtra.stock.readjust') : t('sessionExtra.stock.adjust')}
                    </button>
                  )}
                </div>
              </div>

              {stockMovements.length === 0 ? (
                <div className="stock-movements-empty">
                  {t('sessionExtra.stock.empty')}
                </div>
              ) : (
                <div className="stock-movements-list">
                  {stockMovements.map((movement) => (
                    <div key={movement.id} className="stock-movement-row">
                      <div className="stock-movement-main">
                        <div className="stock-movement-title">
                          <span>{movement.consommable_name || 'Consommable'}</span>
                          <span className={`stock-delta ${Number(movement.quantity_delta) >= 0 ? 'stock-delta-positive' : 'stock-delta-negative'}`}>
                            {formatStockDelta(movement.quantity_delta)}
                          </span>
                        </div>
                        <div className="stock-movement-meta">
                          <span>{movement.movement_type_label || movement.movement_type}</span>
                          {movement.supplier_name && <span>{movement.supplier_name}</span>}
                          {movement.centre_name && <span>{movement.centre_name}</span>}
                        </div>
                        {movement.comment && (
                          <div className="stock-movement-comment">{movement.comment}</div>
                        )}
                      </div>
                      <div className="stock-movement-side">
                        <div className="stock-movement-date">
                          {movement.created_at ? new Date(movement.created_at).toLocaleString(i18n.language) : '-'}
                        </div>
                        <div className="stock-movement-balance">
                          {movement.quantity_before} → {movement.quantity_after}
                        </div>
                        <div className="stock-movement-user">
                          {movement.created_by_name || '-'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contenu de l'onglet Demandes */}
          {showSupplierOrdersTab && activeTab === 'requests' && (
            <div className="supplier-orders-tab-content">
              <div className="supplier-orders-header">
                <div>
                  <h2>{t('sessionExtra.requests.title')}</h2>
                  <p>{t('sessionExtra.requests.subtitle')}</p>
                </div>
                <div className="supplier-orders-header-actions">
                  <span className="supplier-orders-count">
                    {t('sessionExtra.requests.count', { count: supplierOrders.length })}
                  </span>
                  {canManageSupplierOrders && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleSyncSupplierOrders}
                      disabled={supplierOrdersSyncing}
                    >
                      {supplierOrdersSyncing ? t('sessionExtra.requests.syncing') : t('sessionExtra.requests.sync')}
                    </button>
                  )}
                </div>
              </div>

              {supplierOrders.length === 0 ? (
                <div className="supplier-orders-empty">
                  <strong>{t('sessionExtra.requests.emptyTitle')}</strong>
                  <span>
                    {t('sessionExtra.requests.emptyText')}
                  </span>
                  {canManageSupplierOrders && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSyncSupplierOrders}
                      disabled={supplierOrdersSyncing}
                    >
                      {supplierOrdersSyncing ? t('sessionExtra.requests.syncing') : t('sessionExtra.requests.createMissing')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="supplier-orders-list">
                  {supplierOrders.map((order) => (
                  <div key={order.order_id} className="supplier-order-card">
                    <div className="supplier-order-card-header">
                      <div className="supplier-order-title-block">
                        <div className="supplier-order-title">
                          {order.supplier_name || 'Fournisseur'}
                          {Number(order.version || 1) > 1 && (
                            <span className="supplier-order-version">V{order.version}</span>
                          )}
                        </div>
                        <div className="supplier-order-meta">
                          {order.order_type_label && <span>{order.order_type_label}</span>}
                          {order.centre_name && <span>{order.centre_name}</span>}
                          {order.created_at && <span>{t('sessionExtra.requests.createdOn', { date: formatTimestamp(order.created_at) })}</span>}
                          {order.created_by_name && <span>{t('sessionExtra.requests.createdBy', { name: order.created_by_name })}</span>}
                        </div>
                      </div>

                      <div className="supplier-order-status-actions">
                        <span className={`supplier-order-status ${getSupplierOrderStatusClass(order.status)}`}>
                          {order.status_display || getSupplierOrderStatusLabel(order.status)}
                        </span>
                        {canManageSupplierOrders && (
                          <select
                            className="supplier-order-status-select"
                            value={order.status || 'NEW'}
                            onChange={(event) => handleUpdateSupplierOrderStatus(order.order_id, event.target.value)}
                          >
                            {supplierOrderStatusOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    <div className="supplier-order-summary">
                      <div>
                        <span>{t('sessionExtra.requests.desiredDate')}</span>
                        <strong>
                          {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString(i18n.language) : '-'}
                        </strong>
                      </div>
                      <div>
                        <span>{t('sessionExtra.requests.total')}</span>
                        <strong>{toMoney(order.total_devis || 0, order.devise)}</strong>
                      </div>
                      <div>
                        <span>{t('sessionExtra.requests.lastStep')}</span>
                        <strong>
                          {order.invoiced_at
                            ? t('sessionExtra.requests.invoiceGeneratedOn', { date: formatTimestamp(order.invoiced_at) })
                            : order.received_at
                              ? t('sessionExtra.requests.receivedOn', { date: formatTimestamp(order.received_at) })
                              : order.shipped_at
                                ? t('sessionExtra.requests.shippedOn', { date: formatTimestamp(order.shipped_at) })
                                : order.accepted_at
                                  ? t('sessionExtra.requests.acceptedOn', { date: formatTimestamp(order.accepted_at) })
                                  : t('sessionExtra.requests.waitingSupplier')}
                        </strong>
                      </div>
                    </div>

                    {order.adjustment_note && (
                      <div className="supplier-order-note">{order.adjustment_note}</div>
                    )}

                    <div className="supplier-order-lines">
                      {(order.lines || []).map((line) => (
                        <div key={line.id} className="supplier-order-line">
                          <div>
                            <strong>{line.consommable_name}</strong>
                            {line.calculation_detail && <span>{line.calculation_detail}</span>}
                            {line.comment && <span>{line.comment}</span>}
                          </div>
                          <div className="supplier-order-line-total">
                            <span>{line.quantity} x {toMoney(line.unit_price || 0, order.devise)}</span>
                            <strong>{toMoney(line.line_total || 0, order.devise)}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contenu de l'onglet Documents */}
          {!isFournisseur && activeTab === 'documents' && (
            <div className="documents-tab-content">
              <div className="documents-tab-header">
                <div>
                  <h2><Paperclip size={20} /> {t('sessionExtra.documents.title')}</h2>
                  <p>{t('sessionExtra.documents.subtitle')}</p>
                </div>
                {!isFournisseur && (
                  <label className={`btn btn-secondary document-upload-btn ${documentsUploading ? 'disabled' : ''}`}>
                    <Upload size={16} />
                    {documentsUploading ? t('sessionExtra.documents.uploading') : t('sessionExtra.documents.upload')}
                    <input
                      type="file"
                      multiple
                      onChange={handleUploadDocuments}
                      disabled={documentsUploading}
                    />
                  </label>
                )}
              </div>

              {documents.length === 0 ? (
                <div className="session-documents-empty">{t('sessionExtra.documents.empty')}</div>
              ) : (
                <div className="documents-workspace">
                  <div className="documents-list-panel">
                    {documents.map((document) => {
                      const url = getDocumentUrl(document);
                      const isSelected = String(selectedDocument?.id) === String(document.id);
                      return (
                        <button
                          key={document.id}
                          type="button"
                          className={`document-list-item ${isSelected ? 'active' : ''}`}
                          onClick={() => setDocumentPreviewId(document.id)}
                        >
                          <FileText size={18} />
                          <span className="document-list-text">
                            <span className="session-document-name">{document.original_name}</span>
                            <span className="session-document-meta">
                              {document.content_type || t('sessionExtra.documents.file')} • {formatFileSize(document.size)}
                            </span>
                          </span>
                          {url && (
                            <span className="document-list-eye" aria-hidden="true">
                              <Eye size={15} />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="document-preview-panel">
                    <div className="document-preview-header">
                      <div>
                        <h3>{selectedDocument?.original_name || t('sessionExtra.documents.title')}</h3>
                        {selectedDocument && (
                          <p>{selectedDocument.content_type || t('sessionExtra.documents.file')} • {formatFileSize(selectedDocument.size)}</p>
                        )}
                      </div>
                      {selectedDocument && (
                        <div className="session-document-actions">
                          <a
                            href={getDocumentUrl(selectedDocument)}
                            className="btn btn-sm btn-secondary"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink size={14} /> {t('sessionExtra.documents.open')}
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleDownloadDocument(selectedDocument)}
                          >
                            <Download size={14} /> {t('sessionExtra.documents.download')}
                          </button>
                          {!isFournisseur && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteDocument(selectedDocument.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {renderDocumentPreview(selectedDocument)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contenu de l'onglet Historique */}
          {activeTab === 'history' && (
            <div className="history-tab-content">
              {historyLoading && (
                <div className="history-loading">{t('common.loading')}</div>
              )}

              {!historyLoading && historyLogs.length === 0 && (
                <div className="history-empty">{t('history.empty')}</div>
              )}

              {!historyLoading && historyLogs.length > 0 && (
                <>
                  <div className="history-list">
                    {historyLogs.map((log) => (
                      <div key={log.id} className="history-item">
                        <div className="history-item-header">
                          <div className="history-action">
                            <GitCommit size={16} />
                            <span className="history-action-text">{t(`history.actions.${log.action}`)}</span>
                          </div>
                          <div className="history-meta">
                            <span className="history-timestamp">{formatTimestamp(log.timestamp)}</span>
                          </div>
                        </div>

                        <div className="history-item-sub">
                          <div className="history-actor">
                            <User size={14} />
                            <span>{log.actor_name || '-'}</span>
                          </div>
                        </div>

                        {renderChanges(log)}
                      </div>
                    ))}
                  </div>

                  <div className="history-pagination">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => fetchHistory(Math.max(1, historyPage - 1))}
                      disabled={!historyPrevious || historyLoading || historyPage <= 1}
                    >
                      {t('common.previous')}
                    </button>
                    <span className="history-page-info">{t('history.page')} {historyPage}</span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => fetchHistory(historyPage + 1)}
                      disabled={!historyNext || historyLoading}
                    >
                      {t('common.next')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {(isAdmin || isManager) && session.status === 'VERIFIE' && (
        <div className="validation-actions">
          <h3>{t('sessions.detail.decision.title')}</h3>
          <p>{t('sessions.detail.decision.subtitle')}</p>
          <div className="action-buttons">
            {session.status !== 'VALIDEE' && (
              <button onClick={() => setShowValidateConfirm(true)} className="btn btn-success btn-lg">
                {t('sessions.detail.decision.validate')}
              </button>
            )}
            {isManager && (
              <button onClick={() => setShowManagerModifForm(true)} className="btn btn-warning btn-lg">
                {t('sessions.detail.actions.requestModification', { defaultValue: 'Demander modification' })}
              </button>
            )}
            {session.status !== 'REFUSEE' && (
              <button onClick={() => setShowRejectForm(true)} className="btn btn-danger btn-lg">
                {t('sessions.detail.decision.reject')}
              </button>
            )}
          </div>

          {showValidateConfirm && (
            <div className="confirm-dialog">
              <p>{t('sessions.detail.confirm.validate')}</p>

              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={validateApplyToAll}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setValidateApplyToAll(checked);
                        if (checked && validateDeliveryDateAll) applyAllDeliveryDate(validateDeliveryDateAll);
                      }}
                    />
                    <span>{t('sessionExtra.validation.applyDateToAllSuppliers')}</span>
                  </label>
                </div>

                {validateApplyToAll && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={validateUseFirstDayAll}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setValidateUseFirstDayAll(checked);
                            if (checked) {
                              const firstDay = session?.start_date || '';
                              setValidateDeliveryDateAll(firstDay);
                              if (firstDay) applyAllDeliveryDate(firstDay);
                            } else {
                              setValidateDeliveryDateAll('');
                              setSupplierDeliveryDates({});
                            }
                          }}
                          disabled={!session?.start_date}
                        />
                        <span>{t('sessionExtra.validation.useFirstTrainingDay')}</span>
                      </label>
                    </div>

                    <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <label style={{ fontWeight: 700 }}>{t('sessionExtra.validation.deliveryDate')}</label>
                      <input
                        type="date"
                        value={validateDeliveryDateAll || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setValidateDeliveryDateAll(v);
                          setValidateUseFirstDayAll(false);
                          if (v) applyAllDeliveryDate(v);
                        }}
                        style={{
                          padding: '0.5rem 0.6rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                        }}
                      />
                    </div>
                  </div>
                )}

                {!validateApplyToAll && (
                  <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                    {(session?.budget_by_supplier || []).map((g) => (
                      <div key={g.supplier_id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '0.75rem' }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>{g.supplier_name}</div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.25rem' }}>
                            <input
                              type="checkbox"
                              checked={supplierDeliveryDates[g.supplier_id] === (session?.start_date || '') && Boolean(session?.start_date)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const firstDay = session?.start_date || '';
                                if (checked && firstDay) {
                                  setSupplierDeliveryDates((prev) => ({ ...prev, [g.supplier_id]: firstDay }));
                                } else {
                                  setSupplierDeliveryDates((prev) => ({ ...prev, [g.supplier_id]: '' }));
                                }
                              }}
                              disabled={!session?.start_date}
                            />
                            <span>{t('sessionExtra.validation.useFirstTrainingDay')}</span>
                          </label>
                        </div>
                        <input
                          type="date"
                          value={supplierDeliveryDates[g.supplier_id] || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSupplierDeliveryDates((prev) => ({ ...prev, [g.supplier_id]: v }));
                          }}
                          style={{
                            padding: '0.5rem 0.6rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="confirm-actions">
                <button onClick={handleValidate} className="btn btn-success" disabled={deliveryDatesSaving}>
                  {t('common.confirm')}
                </button>
                <button onClick={() => setShowValidateConfirm(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {showResetConfirm && (
            <div className="confirm-dialog">
              <p>{t('sessions.detail.confirm.reset')}</p>
              <div className="confirm-actions">
                <button onClick={() => handleStatusChange('EN_ATTENTE')} className="btn btn-warning">
                  {t('common.confirm')}
                </button>
                <button onClick={() => setShowResetConfirm(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {showRejectForm && (
            <div className="reject-form">
              <label htmlFor="reason">{t('sessions.detail.rejectForm.label')}</label>
              <textarea
                id="reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows="3"
                className="form-input"
                placeholder={t('sessions.detail.rejectForm.placeholder')}
              />
              <div className="reject-actions">
                <button onClick={() => handleStatusChange('REFUSEE')} className="btn btn-danger" disabled={!rejectReason.trim()}>
                  {t('sessions.detail.rejectForm.confirm')}
                </button>
                <button onClick={() => setShowRejectForm(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {showManagerModifForm && (
            <div className="reject-form">
              <label htmlFor="manager_modif">{t('sessions.detail.requestModificationForm.label', { defaultValue: 'Note de modification' })}</label>
              <textarea
                id="manager_modif"
                value={managerModifNote}
                onChange={(e) => setManagerModifNote(e.target.value)}
                rows="3"
                className="form-input"
                placeholder={t('sessions.detail.requestModificationForm.placeholder')}
              />
              <div className="reject-actions">
                <button
                  onClick={async () => {
                    try {
                      await sessionService.requestModificationManager(id, (managerModifNote || '').trim());
                      await refreshSession();
                      setShowManagerModifForm(false);
                      setManagerModifNote('');
                      toast.success(t('status.session.DEMANDE_MODIF_MANAGER'));
                    } catch (e) {
                      toast.error(t('sessions.detail.errors.statusChange'));
                    }
                  }}
                  className="btn btn-warning"
                  disabled={!managerModifNote.trim()}
                >
                  {t('common.confirm')}
                </button>
                <button
                  onClick={() => {
                    setShowManagerModifForm(false);
                    setManagerModifNote('');
                  }}
                  className="btn btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isResponsable && session.status !== 'ANNULEE' && (
        <div className="validation-actions">
          <h3>{(session.status === 'DEMANDE_MODIF' || session.status === 'DEMANDE_MODIF_MANAGER') ? t('sessionExtra.validation.modificationRequested') : t('sessionExtra.validation.availableActions')}</h3>
          <p>
            {(session.status === 'DEMANDE_MODIF' || session.status === 'DEMANDE_MODIF_MANAGER')
              ? t('sessionExtra.validation.editAndResubmitHint')
              : t('sessionExtra.validation.cancelHint')}
          </p>
          <div className="action-buttons">
            {(session.status === 'DEMANDE_MODIF' || session.status === 'DEMANDE_MODIF_MANAGER') && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/sessions/${id}/edit`)}
                  className="btn btn-warning btn-lg"
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await sessionService.resubmitForVerification(id);
                      const updated = await sessionService.getById(id);
                      setSession(updated);
                      toast.success(t('status.session.VERIFICATION'));
                    } catch (e) {
                      toast.error(t('sessions.detail.errors.statusChange'));
                    }
                  }}
                  className="btn btn-success btn-lg"
                >
                  {t('sessionExtra.validation.resubmit')}
                </button>
              </>
            )}
            <button 
              onClick={async () => {
                try {
                  await sessionService.cancel(id);
                  const updated = await sessionService.getById(id);
                  setSession(updated);
                  toast.success(t('sessionExtra.validation.cancelSuccess'));
                } catch (error) {
                  toast.error(t('sessionExtra.validation.cancelError'));
                }
              }} 
              className="btn btn-danger btn-lg"
              style={{ width: (session.status === 'DEMANDE_MODIF' || session.status === 'DEMANDE_MODIF_MANAGER') ? undefined : '100%' }}
              type="button"
            >
              {t('sessionExtra.validation.cancelSession')}
            </button>
          </div>
        </div>
      )}

      {(isLogistiqueCoordinator || isAdmin) && session.status === 'VERIFICATION' && (
        <div className="validation-actions">
          <h3>{t('sessionExtra.validation.verification')}</h3>
          <p>{t('sessionExtra.validation.chooseAction')}</p>
          <div className="action-buttons">
            <button onClick={handleLogMarkVerified} className="btn btn-success btn-lg">
              {t('sessionExtra.validation.markVerified')}
            </button>
            <button onClick={() => { setShowLogNoteForm(true); setShowLogRejectForm(false); }} className="btn btn-warning btn-lg">
              {t('sessionExtra.validation.requestModification')}
            </button>
            <button
              onClick={async () => {
                try {
                  await sessionService.cancel(id);
                  const updated = await sessionService.getById(id);
                  setSession(updated);
                  toast.success(t('sessionExtra.validation.cancelSuccess'));
                } catch (error) {
                  toast.error(t('sessionExtra.validation.cancelError'));
                }
              }}
              className="btn btn-danger btn-lg"
              type="button"
            >
              {t('sessionExtra.validation.cancelSession')}
            </button>
            <button onClick={() => { setShowLogRejectForm(true); setShowLogNoteForm(false); }} className="btn btn-danger btn-lg">
              {t('sessionExtra.additionalFees.reject')}
            </button>
          </div>

          {showLogNoteForm && (
            <div className="reject-form">
              <label htmlFor="log_note">{t('sessionExtra.validation.note')}</label>
              <textarea
                id="log_note"
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                rows="3"
                className="form-input"
                placeholder={t('sessionExtra.validation.requestModificationPlaceholder')}
              />
              <div className="reject-actions">
                <button onClick={handleLogRequestModification} className="btn btn-warning" disabled={!logNote.trim()}>
                  {t('sessionExtra.validation.send')}
                </button>
                <button onClick={() => { setShowLogNoteForm(false); setLogNote(''); }} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {showLogRejectForm && (
            <div className="reject-form">
              <label htmlFor="log_reject">{t('sessionExtra.validation.rejectReasonOptional')}</label>
              <textarea
                id="log_reject"
                value={logRejectReason}
                onChange={(e) => setLogRejectReason(e.target.value)}
                rows="3"
                className="form-input"
                placeholder={t('sessionExtra.validation.rejectReasonPlaceholder')}
              />
              <div className="reject-actions">
                <button onClick={handleLogReject} className="btn btn-danger">
                  {t('sessionExtra.validation.confirmReject')}
                </button>
                <button onClick={() => { setShowLogRejectForm(false); setLogRejectReason(''); }} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setInvoiceWizardFlowActive(false);
        }}
        onGenerate={handleGenerateInvoice}
        session={session}
        mode={invoiceModalMode}
        supplierName={invoiceModalSupplierName}
        suppliers={isAdmin && invoiceWizardFlowActive ? invoiceWizardSuppliers : null}
        activeSupplierId={isAdmin && invoiceWizardFlowActive ? invoiceModalSupplierId : null}
        onSupplierChange={isAdmin && invoiceWizardFlowActive ? handleWizardSupplierChange : null}
        versionOptions={invoiceModalMode === 'single' ? getInvoiceVersionOptions(invoiceModalSupplierId) : []}
        closeOnGenerate={!(isAdmin && invoiceWizardFlowActive)}
      />
    </div>
  );
};

export default SessionDetailPage;
